// آغاز رمضان و شوال در ایران — رصد در برابر محاسبهٔ جدولی
//
// Observed start of رمضان (month 9) and شوال (month 10) in Iran, from 1399 AH
// (July 1979) to 1447 AH (March 2026). These two months decide when fasting
// begins and when عید فطر falls, so they are where a purely tabular converter
// is most visibly wrong.
//
// Same shape and meaning as BUILT_IN_ADJUSTMENTS in ./hijri-adjustments.ts:
//
//   [hy, hm, +1]  the month began one day LATER than calculated
//   [hy, hm, -1]  the month began one day EARLIER than calculated
//
// A month that matched the calculation needs no entry. 44 of the 98 months
// here deviate — 28 late and 16 early. Deviating is normal rather than
// exceptional: the tabular calendar starts a month near conjunction, while Iran
// requires the crescent to be sighted after sunset.
//
// SOURCE. Start dates come from roozbehp/qamari (consolidated.txt), which
// records the first day of each Hijri month as actually used in Iran with a
// citation per month — the contemporary press (روزنامهٔ اطلاعات) up to 1422,
// then مرکز تقویم مؤسسهٔ ژئوفیزیک دانشگاه تهران from 1423 on. The shift itself is
// computed against the Fourmilab algorithm in ../services/hijri.ts, so these
// numbers are exact for this app's arithmetic rather than someone else's.
//
// Cross-checked against the official calendars at calendar.ut.ac.ir: 1 اسفند
// 1404 is printed as 2 رمضان 1447 / 20 Feb 2026, and 1 اسفند 1403 as 20 شعبان
// 1446 / 19 Feb 2025. Both agree with the table.
//
// OBSERVED vs PRINTED (‡). The printed calendar is a prediction made months
// ahead; the announcement on the night is what Iran actually did. Where news
// exists it overrides the printed calendar, and this file always keeps the
// observed day. qamari flags seven months in this range where the two are known
// to have diverged:
//
//   1432/9   2011-08-02  confirmed — the International Astronomical Center puts
//                        Iran on Tue 2 Aug against 25 countries on Mon 1 Aug,
//                        and the Isfahan report for 31 Jul records no crescent
//   1440/9   2019-05-07  confirmed — Khamenei's office found no proven sighting
//                        and declared 6 May the last day of شعبان
//   1425/10  2004-11-14  confirmed — RFE/RL, 14 Nov 2004: state media in Iran
//                        said the feast started that day
//   1443/10  2022-05-03  confirmed — Iran International: the crescent was not
//                        sighted, so Iranians fasted a 30th day while most
//                        countries marked Eid on 2 May
//   1401/9   1981-07-03  qamari only (روزنامهٔ اطلاعات), no online announcement
//   1423/9   2002-11-07  qamari only, no online announcement
//   1426/9   2005-10-05  qamari only, no online announcement
//
// Three of those seven — 1401/9, 1425/10 and 1443/10 — have no entry below,
// because the observed day matched the calculation and it was the printed
// calendar alone that was out of step.
//
// RANGE. Nothing outside 1399–1447 is covered, and hijri.ts treats an unlisted
// month as tabular. A converter given a date far outside this window returns an
// uncorrected result with no signal that it did.
import { HijriAdjustment } from './hijri-adjustments'

export const OBSERVED_ADJUSTMENTS: HijriAdjustment[] = [
    [1399, 10, -1],    // 1979-08-24  عید فطر 2 شهریور 1358
    [1400, 10, -1],    // 1980-08-12  عید فطر 21 مرداد 1359
    [1402, 10, -1],    // 1982-07-22  عید فطر 31 تیر 1361
    [1403, 9,  1],     // 1983-06-13  رمضان   23 خرداد 1362
    [1404, 9,  1],     // 1984-06-01  رمضان   11 خرداد 1363
    [1404, 10,  1],    // 1984-07-01  عید فطر 10 تیر 1363
    [1405, 9,  1],     // 1985-05-22  رمضان   1 خرداد 1364
    [1406, 9,  1],     // 1986-05-11  رمضان   21 اردیبهشت 1365
    [1407, 10, -1],    // 1987-05-29  عید فطر 8 خرداد 1366
    [1408, 10, -1],    // 1988-05-17  عید فطر 27 اردیبهشت 1367
    [1409, 9,  1],     // 1989-04-08  رمضان   19 فروردین 1368
    [1411, 9,  1],     // 1991-03-18  رمضان   27 اسفند 1369
    [1412, 9,  1],     // 1992-03-06  رمضان   16 اسفند 1370
    [1413, 10, -1],    // 1993-03-24  عید فطر 4 فروردین 1372
    [1414, 10, -1],    // 1994-03-13  عید فطر 22 اسفند 1372
    [1415, 10, -1],    // 1995-03-02  عید فطر 11 اسفند 1373
    [1416, 10, -1],    // 1996-02-20  عید فطر 1 اسفند 1374
    [1417, 9,  1],     // 1997-01-11  رمضان   22 دی 1375
    [1418, 10, -1],    // 1998-01-29  عید فطر 9 بهمن 1376
    [1419, 10, -1],    // 1999-01-18  عید فطر 28 دی 1377
    [1420, 9,  1],     // 1999-12-10  رمضان   19 آذر 1378
    [1421, 10, -1],    // 2000-12-27  عید فطر 7 دی 1379
    [1422, 10, -1],    // 2001-12-16  عید فطر 25 آذر 1380
    [1423, 9,  1],     // 2002-11-07  رمضان   16 آبان 1381  ‡
    [1425, 9,  1],     // 2004-10-16  رمضان   25 مهر 1383
    [1426, 9,  1],     // 2005-10-05  رمضان   13 مهر 1384  ‡
    [1426, 10,  1],    // 2005-11-04  عید فطر 13 آبان 1384
    [1427, 9,  1],     // 2006-09-25  رمضان   3 مهر 1385
    [1429, 10, -1],    // 2008-10-01  عید فطر 10 مهر 1387
    [1430, 10, -1],    // 2009-09-20  عید فطر 29 شهریور 1388
    [1431, 9,  1],     // 2010-08-12  رمضان   21 مرداد 1389
    [1432, 9,  1],     // 2011-08-02  رمضان   11 مرداد 1390  ‡
    [1433, 9,  1],     // 2012-07-21  رمضان   31 تیر 1391
    [1434, 9,  1],     // 2013-07-10  رمضان   19 تیر 1392
    [1434, 10,  1],    // 2013-08-09  عید فطر 18 مرداد 1392
    [1437, 10, -1],    // 2016-07-06  عید فطر 16 تیر 1395
    [1439, 9,  1],     // 2018-05-17  رمضان   27 اردیبهشت 1397
    [1440, 9,  1],     // 2019-05-07  رمضان   17 اردیبهشت 1398  ‡
    [1441, 9,  1],     // 2020-04-25  رمضان   6 اردیبهشت 1399
    [1442, 9,  1],     // 2021-04-14  رمضان   25 فروردین 1400
    [1445, 9,  1],     // 2024-03-12  رمضان   22 اسفند 1402
    [1446, 9,  1],     // 2025-03-02  رمضان   12 اسفند 1403
    [1447, 9,  1],     // 2026-02-19  رمضان   30 بهمن 1404
    [1447, 10,  1],    // 2026-03-21  عید فطر 1 فروردین 1405
]
